import { cloneDeep, filter, find, sortBy } from "lodash";
import moment from "moment";

import { ADD, REMOVE } from "../consts/map";
import { COLLECTIONS_PLAN_TYPE, DEFAULT_PLAN_TYPE } from "../consts/planTypes";

import { POSITION_PER_MARK } from "./timeSlider";
import { getRedirectionRouteCode, processRoutesProfile } from "./routes";
import { getTimestampFromHHMM } from "./date";
import { getRedirectConfig } from "./redirectConfig";
import { timeBandsSorter } from "./routeMatrix";

export const getWavesFromRoutes = (routes = [], type = DEFAULT_PLAN_TYPE) => {
  if (type === COLLECTIONS_PLAN_TYPE) return [];
  return [
    ...new Set(
      routes
        .filter((route) => route.routeType === type)
        .map((route) => route.plannedReadyTimeAsString)
    ),
  ]
    .sort()
    .map((wave, index) => ({ waveTime: wave, waveNo: index }));
};

export const updateWaves = ({ waves = [], time }) => {
  const waveToRemove = find(waves, (wave) => wave.waveTime === time);
  return waveToRemove
    ? { waves: removeWave({ waves, time }), action: REMOVE }
    : { waves: addWave({ waves, time }), action: ADD };
};

const removeWave = ({ waves = [], time }) =>
  filter(waves, (wave) => wave.waveTime !== time);

const addWave = ({ waves = [], time }) => {
  const newWaves = [...waves];
  newWaves.push({ waveTime: time });
  return sortBy(newWaves, "waveTime").map((wave, index) => ({
    ...wave,
    waveNo: index,
  }));
};

export const replaceWave = ({ waves = [], wave }) => {
  const newWaves = filter(waves, (w) => w.waveTime !== wave.waveTime);
  newWaves.push(wave);
  return sortBy(newWaves, [(wave) => wave.newWaveTime || wave.waveTime]).map(
    (wave, index) => ({
      ...wave,
      waveNo: index,
    })
  );
};

export const getMinMaxForWave = ({ waves, index, duration }) => {
  let min = 0;
  let max = 100;

  if (index > 0) {
    min = waves[index - 1].position + POSITION_PER_MARK[duration];
  }
  if (index < waves.length - 1) {
    max = waves[index + 1].position - POSITION_PER_MARK[duration];
  }
  return { min, max };
};

export const applyWaveMove = async ({
  currentWaveTime,
  newWaveTime,
  routes,
  routeConfig,
  planType,
  lookupTables,
  overrideAreas,
  pickupAreas,
  locationConfig,
  volumeData,
}) => {
  const currentRedirectionRouteCode = getRedirectionRouteCode(
    planType,
    currentWaveTime
  );
  const newRedirectionRouteCode = getRedirectionRouteCode(
    planType,
    newWaveTime
  );

  routes = applyWaveMoveToRoutes({
    currentWaveTime,
    newWaveTime,
    routes,
    planType,
    currentRedirectionRouteCode,
    newRedirectionRouteCode,
  });

  let redirectConfig;
  ({ routeConfig, redirectConfig } = applyWaveMoveToConfig({
    routeConfig,
    lookupTables,
    currentWaveTime,
    newWaveTime,
    currentRedirectionRouteCode,
    newRedirectionRouteCode,
    planType,
  }));

  const {
    routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  } = await processRoutesProfile({
    routeConfig,
    redirectConfig,
    routes,
    volumeData,
    overrideAreas,
    pickupAreas,
    locationConfig,
    lookupTables,
    planType,
  });

  return {
    routes: routesWithVolume,
    routeConfig,
    redirectConfig,
    waves: getWavesFromRoutes(routesWithVolume, planType),
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  };
};

const applyWaveMoveToRoutes = ({
  currentWaveTime,
  newWaveTime,
  routes,
  planType,
  currentRedirectionRouteCode,
  newRedirectionRouteCode,
}) =>
  routes.map((route) => {
    if (
      currentWaveTime !== route.plannedReadyTimeAsString ||
      route.routeType !== planType
    )
      return {
        ...route,
        routeOptimisation: null,
      };
    if (currentRedirectionRouteCode === route.routeCode)
      return {
        ...route,
        routeCode: newRedirectionRouteCode,
        plannedReadyTime: getTimestampFromHHMM(newWaveTime),
        plannedReadyTimeAsString: newWaveTime,
        routeOptimisation: null,
      };
    else
      return {
        ...route,
        plannedReadyTime: getTimestampFromHHMM(newWaveTime),
        plannedReadyTimeAsString: newWaveTime,
        routeOptimisation: null,
      };
  });

export const applyWaveMoveToConfig = ({
  routeConfig,
  lookupTables,
  currentWaveTime,
  newWaveTime,
  currentRedirectionRouteCode,
  newRedirectionRouteCode,
  planType,
}) => {
  let currentWaveConfig;
  let newWaveConfig;
  const newRouteConfig = [];
  const outersToMove = [];
  const outersNotToMove = [];

  routeConfig.forEach((config) => {
    const waveConfigTime = moment(config.plannedReadyTimeBand).format("HH:mm");
    if (currentWaveTime === waveConfigTime) currentWaveConfig = { ...config };
    else if (newWaveTime === waveConfigTime) newWaveConfig = { ...config };
    else newRouteConfig.push(config);
  });

  currentWaveConfig.outer.forEach((outer) => {
    if (outer.routeType !== planType) return outersNotToMove.push(outer);

    outer = cloneDeep(outer);
    if (outer.sector)
      outer.sector = outer.sector.map((sector) => {
        if (sector.routeCode !== currentRedirectionRouteCode) return sector;
        else
          return {
            ...sector,
            routeCode: newRedirectionRouteCode,
          };
      });
    if (outer.postcode)
      outer.postcode = outer.postcode.map((postcode) => {
        if (postcode.routeCode !== currentRedirectionRouteCode) return postcode;
        else
          return {
            ...postcode,
            routeCode: newRedirectionRouteCode,
          };
      });

    outersToMove.push(outer);
  });

  if (outersNotToMove.length) {
    currentWaveConfig.outer = outersNotToMove;
    newRouteConfig.push(currentWaveConfig);
  }
  if (newWaveConfig)
    newRouteConfig.push({
      ...newWaveConfig,
      outer: newWaveConfig.outer.concat(outersToMove),
    });
  else
    newRouteConfig.push({
      plannedReadyTimeBand: getTimestampFromHHMM(newWaveTime),
      outer: outersToMove,
    });

  newRouteConfig.sort(timeBandsSorter);

  return {
    routeConfig: newRouteConfig,
    redirectConfig: getRedirectConfig(newRouteConfig, lookupTables),
  };
};
